import { ref, computed } from 'vue';
import { useMattersStore } from '@/stores/useMattersStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useEntryProcessState } from '@/composables/useEntryProcessState';

interface BulkOperationErrorItem {
  itemId: string;
  reason: string;
}

interface BulkOperationResult {
  successCount: number;
  totalCount: number;
  errors: BulkOperationErrorItem[];
}

interface UseMattersBulkOperationsOptions {
  getMatterIds: () => string[];
  clearSelection: () => void;
  onAfterOperation?: () => Promise<void>;
  // Defaults to 'Matter'.
  getMatterTerm?: () => string;
}

/**
 * Composable for the Matters list's bulk actions (Change Responsible,
 * Archive/Unarchive, Apply Rate Group, Enable/Disable LEDES).
 *
 * Mirrors useFixedFeeBulkOperations.js's shape: owns confirmation-dialog
 * refs, the embedded-picker selections those dialogs need, loading state,
 * and per-action handlers that call the store, interpret the result via
 * useEntryProcessState (itemId/reason match the backend's
 * BulkOperationError.ItemId/.Reason - ASP.NET Core's default System.Text.Json
 * serializer camelCases them on the way out; axios/the frontend does no
 * case transformation of its own), show a success/partial-failure snackbar,
 * clear the selection, and refresh.
 */
export function useMattersBulkOperations({
  getMatterIds,
  clearSelection,
  onAfterOperation,
  getMatterTerm = () => 'Matter',
}: UseMattersBulkOperationsOptions) {
  const mattersStore = useMattersStore();
  const { handleWarning, handleSuccess, handleError } = useSnackbarHandler();

  const {
    stateMap: matterProcessStateMap,
    setProcessing: setMattersProcessing,
    clear: clearMatterProcessState,
    handleResult: handleBulkOperationResult,
    setAllFailed,
    getState: getMatterProcessState,
    getRowClass,
  } = useEntryProcessState();

  const bulkOperationLoading = ref(false);

  // ─── Confirmation Dialog Refs ────────────────────────────
  const changeResponsibleConfirmRef = ref(null);
  const archiveConfirmRef = ref(null);
  const rateGroupConfirmRef = ref(null);
  const ledesEnableConfirmRef = ref(null);
  const ledesDisableConfirmRef = ref(null);

  // ─── Embedded Picker Selections ──────────────────────────
  const selectedAccountId = ref<string | null>(null);
  const selectedRateGroupId = ref<string | null>(null);
  // Defaults to true, matching the single-matter Billing and Rates "Update
  // time entries" checkbox's default.
  const selectedUpdateEntries = ref(true);
  const selectedLedesFormat = ref<string | null>(null);

  // ─── Confirmation Messages ───────────────────────────────
  const pluralMatters = (count: number) => `${count} ${getMatterTerm()}${count === 1 ? '' : 's'}`;

  const changeResponsibleConfirmMessage = computed(
    () => `Change the responsible attorney for ${pluralMatters(getMatterIds().length)}?`,
  );

  // Plain function, not computed - the archive/restore direction is only
  // known at confirm time (bound from filters.archived by the caller), so
  // this can't be a zero-arg computed getter.
  const archiveConfirmMessage = (archive: boolean) =>
    `${archive ? 'Archive' : 'Restore'} ${pluralMatters(getMatterIds().length)}?`;

  const rateGroupConfirmMessage = computed(
    () => `Apply the selected rate group to ${pluralMatters(getMatterIds().length)}?`,
  );

  const ledesEnableConfirmMessage = computed(
    () => `Enable LEDES for ${pluralMatters(getMatterIds().length)}?`,
  );

  const ledesDisableConfirmMessage = computed(
    () => `Disable LEDES for ${pluralMatters(getMatterIds().length)}?`,
  );

  // ─── Shared Runner ────────────────────────────────────────
  const runBulkAction = async (
    actionFn: (ids: string[]) => Promise<BulkOperationResult>,
    successVerb: string,
  ) => {
    const ids = getMatterIds();
    if (!ids.length) return;

    bulkOperationLoading.value = true;
    clearMatterProcessState();
    setMattersProcessing(ids);

    try {
      const result = await actionFn(ids);
      const hasErrors = handleBulkOperationResult(ids, result.errors, 'itemId', 'reason');

      const matterText = pluralMatters(ids.length);
      if (hasErrors) {
        handleWarning(
          `${successVerb} ${result.successCount} of ${matterText}. See details for the rest.`,
        );
      } else {
        handleSuccess(`Successfully ${successVerb.toLowerCase()} ${matterText}.`);
      }

      clearSelection();
      await onAfterOperation?.();
    } catch (err) {
      setAllFailed(ids);
      handleError(err);
    } finally {
      bulkOperationLoading.value = false;
    }
  };

  // ─── Change Responsible ───────────────────────────────────
  const confirmChangeResponsible = async () => {
    const confirmed = await changeResponsibleConfirmRef.value?.open();
    if (!confirmed || !selectedAccountId.value) return;
    await runBulkAction(
      (ids) => mattersStore.bulkChangeResponsible(ids, selectedAccountId.value as string),
      'Updated',
    );
    selectedAccountId.value = null;
  };

  // ─── Archive / Unarchive (same endpoint, archive: bool direction) ──
  const confirmArchive = async (archive: boolean) => {
    const confirmed = await archiveConfirmRef.value?.open();
    if (!confirmed) return;
    await runBulkAction(
      (ids) => mattersStore.bulkArchive(ids, archive),
      archive ? 'Archived' : 'Restored',
    );
  };

  // ─── Apply Rate Group ─────────────────────────────────────
  const confirmApplyRateGroup = async () => {
    const confirmed = await rateGroupConfirmRef.value?.open();
    if (!confirmed || !selectedRateGroupId.value) return;
    await runBulkAction(
      (ids) => mattersStore.bulkApplyRateGroup(ids, selectedRateGroupId.value as string, selectedUpdateEntries.value),
      'Updated',
    );
    selectedRateGroupId.value = null;
    selectedUpdateEntries.value = true;
  };

  // ─── Enable / Disable LEDES ───────────────────────────────
  const confirmEnableLedes = async () => {
    const confirmed = await ledesEnableConfirmRef.value?.open();
    if (!confirmed || !selectedLedesFormat.value) return;
    await runBulkAction(
      (ids) => mattersStore.bulkUpdateLedes(ids, true, selectedLedesFormat.value as string),
      'Updated',
    );
    selectedLedesFormat.value = null;
  };

  const confirmDisableLedes = async () => {
    const confirmed = await ledesDisableConfirmRef.value?.open();
    if (!confirmed) return;
    await runBulkAction((ids) => mattersStore.bulkUpdateLedes(ids, false, null), 'Updated');
  };

  return {
    bulkOperationLoading,

    changeResponsibleConfirmRef,
    archiveConfirmRef,
    rateGroupConfirmRef,
    ledesEnableConfirmRef,
    ledesDisableConfirmRef,

    selectedAccountId,
    selectedRateGroupId,
    selectedUpdateEntries,
    selectedLedesFormat,

    changeResponsibleConfirmMessage,
    archiveConfirmMessage,
    rateGroupConfirmMessage,
    ledesEnableConfirmMessage,
    ledesDisableConfirmMessage,

    confirmChangeResponsible,
    confirmArchive,
    confirmApplyRateGroup,
    confirmEnableLedes,
    confirmDisableLedes,

    matterProcessStateMap,
    getMatterProcessState,
    getRowClass,
  };
}
